import React, { useMemo, useState } from 'react';
import {
  MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { Box } from '@mui/material';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { apiClient, apiClientHooks } from "../../bootstrapping/InitApiClient";
import { Helmet } from "react-helmet-async";
import { z } from 'zod';
import { schemas } from '../../generated/api/client';
import { isSomething } from '../../utils/utils';
import { isAxiosError } from 'axios';
import { useAppTable } from '../../components/AppTable';
import { doReloadAuth } from '../../utils/reloadAuth';
import { queryClient } from '../../bootstrapping/InitReactQuery';
import IconButtonWithTooltip from '../../components/IconButtonWithTooltip';
import { useCaseWorkerContext } from '../../components/caseWorkers/caseWorkerContext';
import CaseWorkerAssignDialog from '../../components/caseWorkers/CaseWorkerAssignDialog';

type ProfilesWithMeta = z.infer<typeof schemas.ReturnType_GetProfiles>
type Profile = ProfilesWithMeta['rows'][number]

const profileDisplayName = (profile: Profile): string =>
  `${profile.given_name ?? ''} ${profile.family_name ?? ''}`.trim() || profile.email

export const Element: React.FC = () => {
  const { customerId, myProfileId, isManagerOrStaff, caseWorkersEnabled } = useCaseWorkerContext()
  const canManageCaseWorkers = isManagerOrStaff && caseWorkersEnabled === true
  const tableQueryClient = useQueryClient()
  const [unflagTarget, setUnflagTarget] = useState<Profile | undefined>(undefined)

  const flagAsCaseWorker = async (profile: Profile) => {
    if (!customerId) return
    const confirmed = window.confirm(
      `Make ${profileDisplayName(profile)} a case worker? They will only see the invites and applications they are assigned to.`
    )
    if (!confirmed) return

    await apiClient.setCaseWorkerFlagForProfile(undefined, {
      params: { id: profile.id },
      queries: { customer_id: customerId, case_worker: true },
    })
    await Promise.all([
      tableQueryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getProfiles') }),
      tableQueryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getGlobalStats') }),
    ])
  }

  const unflagCaseWorker = async (profile: Profile, transferToProfileId: string | null) => {
    if (!customerId) return
    const result = await apiClient.setCaseWorkerFlagForProfile(undefined, {
      params: { id: profile.id },
      queries: {
        customer_id: customerId,
        case_worker: false,
        ...(transferToProfileId != null ? { transfer_to_profile_id: transferToProfileId } : {}),
      },
    })
    await Promise.all([
      tableQueryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getProfiles') }),
      tableQueryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getInvites') }),
      tableQueryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getEBulkApplications') }),
      tableQueryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getGlobalStats') }),
    ])
    const destination = transferToProfileId == null ? 'unassigned' : 'transferred'
    window.alert(`${profileDisplayName(profile)} is no longer a case worker. ${result.transferred_invites} invite(s) and ${result.transferred_applications} application(s) were ${destination}.`)
  }

  const {
    tableConfig,
  } = useAppTable<Profile, ProfilesWithMeta, Error | null>({
    useTableQuery: ({ columnFilters, globalFilter, pagination, sorting }) =>
      useQuery<ProfilesWithMeta>({
        queryKey: [
          ...apiClientHooks.getKeyByAlias('getProfiles'),
          columnFilters, //refetch when columnFilters changes
          globalFilter ?? '', //refetch when globalFilter changes
          pagination.pageIndex, //refetch when pagination.pageIndex changes
          pagination.pageSize, //refetch when pagination.pageSize changes
          sorting, //refetch when sorting changes
        ],
        queryFn: async ({ queryKey }) => {
          // Build the sort by string, and ensure it meets the API schema
          const sort = (() => {
            const firstSort = sorting[0]
            if (!firstSort) return undefined

            const compoundSort = `${firstSort.id} ${firstSort.desc ? 'desc' : 'asc'}`
            return schemas.Sort_GetProfiles.parse(compoundSort)
          })()

          try {
            const result = await apiClient.getProfiles({
              queries: {
                page_index: `${pagination.pageIndex}`,
                page_size: `${pagination.pageSize}`,
                sort,
                globalFilter: globalFilter.length > 0 ? globalFilter : undefined,
                columnFilters: schemas.ColumnFilters_GetProfiles.parse(
                  columnFilters
                    .map(filter => {
                      // Check ID is a valid column
                      const parseResult = schemas.ColumnFilters_GetJobs.element.safeParse(filter)
                      if (!parseResult.success && parseResult.error.errors.find(e => e.path[0] === 'id')) {
                        console.error("Invalid column ID for filter: ", filter)
                        return undefined
                      }

                      // Transform Enum Filters

                      // Transform Boolean Filters
                      if (filter.id === 'active' || filter.id === 'allowPayByInvoice') {
                        const valueAsStringBoolean = z.union([z.literal('true'), z.literal('false')]).safeParse(filter.value)
                        if (!valueAsStringBoolean.success) {
                          console.error("Invalid Boolean Filter (value should be string 'true' or 'false'): ", filter)
                          return undefined
                        }

                        return {
                          id: filter.id,
                          value: valueAsStringBoolean.data === 'true' ? true : false,
                        }
                      }

                      // Transform Date Range Filters
                      if (filter.id === 'createdAt' || filter.id === 'updatedAt') {
                        const values = z.array(z.date().optional()).safeParse(filter.value)
                        if (!values.success || values.data.length !== 2) {
                          console.error("Invalid date range filter: ", filter)
                          return undefined
                        }
                        if (values.data[0] === undefined && values.data[1] === undefined) {
                          // Nothing to filter on!
                          return undefined
                        }

                        return {
                          id: filter.id,
                          from: values.data[0]?.toISOString(),
                          to: values.data[1]?.toISOString(),
                        }
                      }

                      // Everything else is just a text filter
                      var parsedValue = z.string().safeParse(filter.value)
                      if (!parsedValue.success) {
                        console.error("Invalid text filter: ", filter)
                        return undefined
                      }

                      return {
                        id: filter.id,
                        value: parsedValue.data,
                      }
                    })
                    .filter(isSomething)
                )
              },
            })

            console.log("Invites.Element.queryFn: ", { result })
            return result
          } catch (error) {
            if (!isAxiosError(error)) {
              console.error("Error fetching profiles (non-Axios): ", error)
              throw error
            }

            const parsedValue = schemas.ErrorResponse.safeParse(error.response?.data)
            if (!parsedValue.success) {
              console.error("Error fetching profiles (unknown error): ", error)
              throw error
            }

            console.error("Error fetching profiles: ", parsedValue.data.error)

            // Do we need to reload the auth tokens?
            if (['jwtExpired', 'errProfileJwtExpired'].includes(parsedValue.data.error.type ?? '')) {
              console.warn('Auth0 / Profile JWT Expired:', parsedValue.data.error);
              await doReloadAuth()
              await queryClient.invalidateQueries({ queryKey })
              return Promise.reject({ message: 'Auth tokens expired. Please refresh the data.' })
            }

            return Promise.reject(parsedValue.data.error)
          }
        },
        placeholderData: keepPreviousData, //don't go to 0 rows when refetching or paginating to next page
      }),
    columns: useMemo<MRT_ColumnDef<Profile>[]>(() => [
      {
        accessorKey: 'email',
        header: 'Email',
        size: 150,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: 'given_name',
        header: 'Given Name',
        size: 150,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: 'family_name',
        header: 'Family Name',
        size: 150,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: 'is_staff',
        accessorFn: (originalRow) => (originalRow.is_staff ? 'true' : 'false'), //must be strings
        header: 'Staff Member',
        size: 150,
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => cell.getValue() === 'true' ? '✅' : '❌',
        filterVariant: 'checkbox',
      },
      {
        accessorKey: 'admin_customer_ids',
        header: 'Admin Customer IDs',
        size: 150,
        enableSorting: false,
        // TODO: Add a filter for this, maybe a custom display
        enableColumnFilter: false,
        _visible: false,
      },
      {
        accessorKey: 'admin_customer_names',
        header: 'Admin Customer Names',
        size: 150,
        enableSorting: false,
        // TODO: Add a filter for this, maybe a custom display
        enableColumnFilter: false,
      },
      {
        accessorKey: 'known_customer_ids',
        header: 'Known Customer IDs',
        size: 150,
        enableSorting: false,
        // TODO: Add a filter for this, maybe a custom display
        enableColumnFilter: false,
        _visible: false,
      },
      {
        accessorKey: 'known_customer_names',
        header: 'Known Customer Names',
        size: 150,
        enableSorting: false,
        // TODO: Add a filter for this, maybe a custom display
        enableColumnFilter: false,
      },
      // Only defined at all when the customer manages case workers
      ...(caseWorkersEnabled === true ? [
        {
          accessorKey: 'case_worker_customer_ids',
          accessorFn: (originalRow: Profile) => ((originalRow.case_worker_customer_ids ?? []).includes(customerId ?? '') ? 'true' : 'false'), //must be strings
          header: 'Case Worker',
          size: 120,
          enableSorting: false,
          enableColumnFilter: false,
          Cell: ({ cell }) => cell.getValue() === 'true' ? '✅' : '',
        } satisfies MRT_ColumnDef<Profile>,
      ] : []),
    ], [customerId, caseWorkersEnabled]),
  })

  const table = useMaterialReactTable({
    ...tableConfig,
    enableRowActions: canManageCaseWorkers,
    renderRowActions: ({ row }) => {
      const profile = row.original
      const isSelf = profile.id === myProfileId
      const isAdminOfCustomer = (profile.admin_customer_ids ?? []).includes(customerId ?? '')
      const isFlagged = (profile.case_worker_customer_ids ?? []).includes(customerId ?? '')
      if (!canManageCaseWorkers || !isAdminOfCustomer) return null

      return (
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          {isFlagged
            ? (
              <IconButtonWithTooltip
                title={isSelf ? 'You cannot change your own case worker flag' : 'Remove case worker flag (transfers their open cases)'}
                icon={PersonRemoveIcon}
                color='warning'
                disabled={isSelf}
                onClick={() => setUnflagTarget(profile)}
              />
            )
            : (
              <IconButtonWithTooltip
                title={isSelf ? 'You cannot change your own case worker flag' : 'Make case worker'}
                icon={SupervisorAccountIcon}
                color='primary'
                disabled={isSelf}
                onClick={() => flagAsCaseWorker(profile)}
              />
            )}
        </Box>
      )
    },
    // muiExpandButtonProps: ({ row }) => ({
    //   children: row.getIsExpanded() ? <MinusIcon /> : <AddIcon />,
    // }),
    // renderDetailPanel: props => <DetailPanel {...props} />,
  });

  return <>
    <Helmet>
      <title>Profiles</title>
    </Helmet>
    <MaterialReactTable table={table} />;
    {unflagTarget && customerId && (
      <CaseWorkerAssignDialog
        open
        title={`Remove case worker flag: ${profileDisplayName(unflagTarget)}`}
        description="They will become a manager and see everything for this customer again. Choose who their open invites and applications should be transferred to."
        customerId={customerId}
        excludeProfileIds={[unflagTarget.id]}
        unassignedLabel="Leave unassigned"
        submitLabel="Remove flag & transfer"
        onClose={() => setUnflagTarget(undefined)}
        onSubmit={transferToProfileId => unflagCaseWorker(unflagTarget, transferToProfileId)}
      />
    )}
  </>
}
